/**
 * AdminDashboardPanel — systemverktøyene til de voksne (Plan 06-04 SC-5).
 *
 * #159 (redesign steg 9c, Stell_Admin): panelet er System-fanen på /foreldre
 * og står også på /settings/admin. Før var det ett stort kort med seks
 * seksjoner under hverandre; nå er det tre glasskort:
 *
 *   1. Kjøkkenskjermer  (<DeviceAdminSection>, #142) — alltid åpent, fordi
 *                        det er det en voksen oftest kommer hit for.
 *   2. AI i dag         (<AssistantAdminPanel>) — dagens kostnad i USD.
 *   3. Rader med drill-down, som åpner seksjonen under seg:
 *        Butikkartikler (<CosmeticAdminSection>) · antall
 *        Push-varsler   (<PushHealthTable>)      · «N av M på»
 *        AI-drift       (<AiTelemetrySection>), som teller siden oppstart
 *        Skjulte belønninger (<HiddenRewardsSection>, #219) · antall — mat-
 *                       belønninger lag 2 skjuler for noen på grunn av allergi
 *        Tving oppdatering (<ForceReloadButton>) — raden åpner
 *                       bekreftelsen direkte, så det er ikke noe å folde ut.
 *        Last ned familiens data (<ExportFamilyButton>, FF-14 #182) — raden
 *                       laster ned zip-fila direkte.
 *        Retningslinjer — lenken til THIRD_PARTY.md (ny fane).
 *
 * Utelatt fra skissen: brikken «Låst opp · N min» (krever utløpstiden til
 * adminopplåsingen på /me) og NOK mot et dagsbudsjett (finnes ikke; kostnaden
 * er i USD og har ikke noe budsjett).
 *
 * Tilgjengelighet: ytterst er en region med aria-label «Administrasjon».
 * Radene er knapper med aria-expanded/aria-controls, som raden for
 * medlemmer på Barna-fanen (FamilyTab). Én rad er åpen om gangen; en
 * seksjon som har vært åpen, blir liggende skjult (`hidden`), så
 * aria-controls alltid peker på et element og et påbegynt skjema ikke
 * forsvinner.
 */
import { useId, useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/primitives';
import { ForceReloadButton } from '../settings/ForceReloadButton';
import { ExportFamilyButton } from './ExportFamilyButton';
import { AssistantAdminPanel } from './AssistantAdminPanel';
import { AiTelemetrySection } from './AiTelemetrySection';
import { PushHealthTable } from './PushHealthTable';
import { CosmeticAdminSection } from './CosmeticAdminSection';
import { DeviceAdminSection } from './DeviceAdminSection';
import { HiddenRewardsSection } from './HiddenRewardsSection';
import { useHiddenRewards } from './useHiddenRewards';
import { useAdminCosmetics } from './useAdminCosmetics';
import { usePushHealth } from './usePushHealth';
import { ADMIN_LIST, ADMIN_ROW } from './adminStyles';

type Drill = 'cosmetics' | 'push' | 'telemetry' | 'hiddenRewards';

export function AdminDashboardPanel(): ReactElement {
  const { t } = useTranslation();
  const baseId = useId();
  const [open, setOpen] = useState<Drill | null>(null);
  // Seksjoner som har vært åpne, blir stående montert (skjult), så et halvferdig
  // skjema i Butikkartikler overlever at forelderen ser på en annen rad.
  const [mounted, setMounted] = useState<ReadonlySet<Drill>>(() => new Set());

  const cosmetics = useAdminCosmetics();
  const push = usePushHealth();
  const hiddenRewards = useHiddenRewards();

  const pushRows = push.data?.rows ?? [];
  const pushValue = push.data
    ? t('admin.rows.push_value', {
        on: pushRows.filter((r) => r.status === 'Aktiv').length,
        total: pushRows.length,
      })
    : null;

  function toggle(key: Drill): void {
    setOpen((cur) => (cur === key ? null : key));
    setMounted((cur) => (cur.has(key) ? cur : new Set(cur).add(key)));
  }

  const drills: Array<{ key: Drill; label: string; value: string | null; body: ReactNode }> = [
    {
      key: 'cosmetics',
      label: t('admin.cosmetics.section_label'),
      value: cosmetics.data ? String(cosmetics.data.length) : null,
      body: <CosmeticAdminSection />,
    },
    {
      key: 'push',
      label: t('admin.rows.push'),
      value: pushValue,
      body: <PushHealthTable />,
    },
    {
      key: 'telemetry',
      label: t('admin.ai_telemetry.section_label'),
      value: null,
      body: <AiTelemetrySection />,
    },
    {
      key: 'hiddenRewards',
      label: t('admin.hiddenRewards.section_label'),
      value: hiddenRewards.data ? String(hiddenRewards.data.length) : null,
      body: <HiddenRewardsSection />,
    },
  ];

  return (
    <section
      role="region"
      aria-label={t('admin.panel.heading')}
      className="flex flex-col gap-3"
      data-testid="admin-dashboard-panel"
    >
      <DeviceAdminSection />

      <AssistantAdminPanel />

      <div className={ADMIN_LIST}>
        {drills.map((d) => {
          const panelId = `${baseId}-${d.key}`;
          const isOpen = open === d.key;
          return (
            <div key={d.key} className="border-b border-card-line">
              <button
                type="button"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => toggle(d.key)}
                data-testid={`admin-row-${d.key}`}
                className={ADMIN_ROW}
              >
                <span className="min-w-0 flex-1">{d.label}</span>
                {d.value ? <span className="tnum font-semibold text-ink-2">{d.value}</span> : null}
                <Icon.ChevR
                  size={18}
                  className={`shrink-0 text-ink-2 ${isOpen ? 'rotate-90' : ''}`}
                />
              </button>
              <div id={panelId} hidden={!isOpen} className="border-t border-card-line py-3">
                {mounted.has(d.key) ? d.body : null}
              </div>
            </div>
          );
        })}

        <ForceReloadButton />

        <ExportFamilyButton />

        {/* Retningslinjer / THIRD_PARTY.md — lenken beholdes (D-13). */}
        <a
          href="/THIRD_PARTY.md"
          target="_blank"
          rel="noopener"
          className={`${ADMIN_ROW} py-2`}
          // Navnet begynner med den synlige etiketten (WCAG 2.5.3).
          aria-label={t('admin.policy.link_aria')}
        >
          <Icon.Shield className="h-4 w-4 shrink-0 text-ink-2" />
          <span className="min-w-0 flex-1">
            <span className="block">{t('admin.policy.section_label')}</span>
            <span className="block text-caption font-semibold text-ink-2">
              {t('admin.policy.link_label')}
            </span>
            <span className="block text-caption font-medium text-ink-2">
              {t('admin.policy.sub_note')}
            </span>
          </span>
          <Icon.ChevR size={18} className="shrink-0 text-ink-2" />
        </a>
      </div>
    </section>
  );
}
